import { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import Button from '../components/ui/Button';
import PageHeader from '../components/PageHeader';
import { Clock, Play, RotateCcw, ArrowRight } from 'lucide-react';
import timesheetService, {
  type PostClockinWorkPayload,
  type PausedJob,
  type PausedSpray,
} from '../services/timesheet';
import jobsService from '../services/jobs';
import sprayService from '../services/spray';
import waypointQueue from '../services/waypointQueue';
import { useOrgTimezone, formatDateTimeInTz, formatLocalDateInTz } from '../utils/timezone';

type Tab = 'auto' | 'manual' | 'pending';

export default function ResumeWork() {
  const navigate = useNavigate();
  const location = useLocation();
  const orgTz = useOrgTimezone();

  // The Timesheet page navigates here with state preloaded; if a user lands
  // on this URL directly we still fetch fresh data.
  const initial = (location.state as PostClockinWorkPayload | null) || null;
  const [data, setData] = useState<PostClockinWorkPayload | null>(initial);
  const [loading, setLoading] = useState<boolean>(initial == null);
  const [error, setError] = useState<string | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('auto');

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await timesheetService.getPostClockinWork();
      if (res.success) setData(res.data);
      else setError(res.error?.message || 'Could not load your paused work.');
    } catch (e: any) {
      setError(e?.message || 'Failed to load paused work');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!initial) refresh();
  }, [initial, refresh]);

  const handleResumeJob = async (job: PausedJob) => {
    setActingId(`job-${job.id}`);
    try {
      await jobsService.resumeJob(job.id);
      // Drain any waypoints that had been stuck behind 409s before the resume.
      void waypointQueue.flush({ drain: true });
      navigate(`/jobs/${job.id}`);
    } catch (e: any) {
      setError(e?.message || `Failed to resume "${job.name}"`);
      setActingId(null);
    }
  };

  const handleStartJob = (job: PausedJob) => {
    // Pending assignments are started on their detail page (pre-start
    // checklist + equipment session can attach there).
    navigate(`/jobs/${job.id}`);
  };

  const handleResumeSpray = async (spray: PausedSpray) => {
    setActingId(`spray-${spray.id}`);
    try {
      await sprayService.resumeApplication(spray.id);
      void waypointQueue.flush({ drain: true });
      navigate(`/spray/${spray.id}`);
    } catch (e: any) {
      setError(e?.message || `Failed to resume "${spray.title}"`);
      setActingId(null);
    }
  };

  const handleStartSpray = (spray: PausedSpray) => {
    navigate(`/spray/${spray.id}`);
  };

  const tabCounts = {
    auto: (data?.auto_paused_jobs.length ?? 0) + (data?.auto_paused_sprays.length ?? 0),
    manual: (data?.manual_paused_jobs.length ?? 0) + (data?.manual_paused_sprays.length ?? 0),
    pending: (data?.pending_jobs.length ?? 0) + (data?.pending_sprays.length ?? 0),
  };

  // Auto-select the first non-empty tab so the user lands on something useful.
  useEffect(() => {
    if (!data) return;
    if (tabCounts.auto === 0) {
      if (tabCounts.pending > 0) setTab('pending');
      else if (tabCounts.manual > 0) setTab('manual');
    }
    // Only run when data first arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const renderJobRow = (job: PausedJob, kind: 'paused' | 'pending') => (
    <div key={`job-${job.id}`} className="flex items-center justify-between gap-4 p-3 border-b last:border-b-0">
      <div className="min-w-0 flex-1">
        <div className="font-medium truncate">{job.name}</div>
        <div className="text-xs text-gray-500 truncate">
          Farm Job{job.farm_name ? ` · ${job.farm_name}` : ''}
          {job.target_name ? ` · ${job.target_name}` : ''}
        </div>
        {kind === 'paused' && job.paused_at ? (
          <div className="text-xs text-gray-400 mt-0.5">
            Paused {formatDateTimeInTz(job.paused_at, orgTz)}
          </div>
        ) : null}
        {kind === 'pending' && job.scheduled_start ? (
          <div className="text-xs text-gray-400 mt-0.5">
            Scheduled {formatDateTimeInTz(job.scheduled_start, orgTz)}
          </div>
        ) : null}
      </div>
      <Button
        size="sm"
        onClick={() => (kind === 'paused' ? handleResumeJob(job) : handleStartJob(job))}
        disabled={actingId === `job-${job.id}`}
      >
        {kind === 'paused' ? (
          <>
            <RotateCcw className="w-4 h-4 mr-1" /> Resume
          </>
        ) : (
          <>
            <Play className="w-4 h-4 mr-1" /> Open
          </>
        )}
      </Button>
    </div>
  );

  const renderSprayRow = (spray: PausedSpray, kind: 'paused' | 'pending') => (
    <div key={`spray-${spray.id}`} className="flex items-center justify-between gap-4 p-3 border-b last:border-b-0">
      <div className="min-w-0 flex-1">
        <div className="font-medium truncate">{spray.title}</div>
        <div className="text-xs text-gray-500 truncate">
          Spray Application{spray.reference_number ? ` · ${spray.reference_number}` : ''}
        </div>
        {kind === 'paused' && spray.paused_at ? (
          <div className="text-xs text-gray-400 mt-0.5">
            Paused {formatDateTimeInTz(spray.paused_at, orgTz)}
          </div>
        ) : null}
        {kind === 'pending' && spray.scheduled_date ? (
          <div className="text-xs text-gray-400 mt-0.5">
            Scheduled {formatLocalDateInTz(spray.scheduled_date, orgTz)}
            {spray.scheduled_start_time ? ` at ${spray.scheduled_start_time}` : ''}
          </div>
        ) : null}
      </div>
      <Button
        size="sm"
        onClick={() => (kind === 'paused' ? handleResumeSpray(spray) : handleStartSpray(spray))}
        disabled={actingId === `spray-${spray.id}`}
      >
        {kind === 'paused' ? (
          <>
            <RotateCcw className="w-4 h-4 mr-1" /> Resume
          </>
        ) : (
          <>
            <Play className="w-4 h-4 mr-1" /> Open
          </>
        )}
      </Button>
    </div>
  );

  const tabClass = (active: boolean) =>
    `px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
      active
        ? 'border-blue-600 text-blue-700'
        : 'border-transparent text-gray-500 hover:text-gray-700'
    }`;

  let body: React.ReactNode;
  if (loading) {
    body = <div className="p-6 text-center text-gray-500">Loading your work…</div>;
  } else if (error) {
    body = <div className="p-6 text-center text-red-600">{error}</div>;
  } else if (!data) {
    body = <div className="p-6 text-center text-gray-500">No work to resume.</div>;
  } else {
    let activeJobs: PausedJob[] = [];
    let activeSprays: PausedSpray[] = [];
    let kind: 'paused' | 'pending' = 'paused';
    if (tab === 'auto') {
      activeJobs = data.auto_paused_jobs;
      activeSprays = data.auto_paused_sprays;
    } else if (tab === 'manual') {
      activeJobs = data.manual_paused_jobs;
      activeSprays = data.manual_paused_sprays;
    } else {
      activeJobs = data.pending_jobs;
      activeSprays = data.pending_sprays;
      kind = 'pending';
    }

    if (activeJobs.length === 0 && activeSprays.length === 0) {
      body = (
        <div className="p-6 text-center text-gray-500 text-sm">
          {tab === 'auto'
            ? 'Nothing was auto-paused at your last clock-out.'
            : tab === 'manual'
            ? 'No manually paused work.'
            : 'No pending assignments.'}
        </div>
      );
    } else {
      body = (
        <div>
          {activeJobs.map((j) => renderJobRow(j, kind))}
          {activeSprays.map((s) => renderSprayRow(s, kind))}
        </div>
      );
    }
  }

  return (
    <>
      <PageHeader
        title="Resume your work"
        subtitle="Pick up where you left off, or jump into a pending assignment."
        icon={<Clock className="h-5 w-5" />}
      />
      <div className="max-w-3xl mx-auto p-4">
        <Card className="mt-4">
          <div className="flex gap-2 px-2 pt-2 border-b">
            <button className={tabClass(tab === 'auto')} onClick={() => setTab('auto')}>
              Auto-paused {tabCounts.auto > 0 ? `(${tabCounts.auto})` : ''}
            </button>
            <button className={tabClass(tab === 'manual')} onClick={() => setTab('manual')}>
              Manually paused {tabCounts.manual > 0 ? `(${tabCounts.manual})` : ''}
            </button>
            <button className={tabClass(tab === 'pending')} onClick={() => setTab('pending')}>
              Pending {tabCounts.pending > 0 ? `(${tabCounts.pending})` : ''}
            </button>
          </div>
          {body}
        </Card>

        <div className="mt-4 flex justify-end">
          <Button variant="outline" onClick={() => navigate('/dashboard')}>
            Skip — go to dashboard <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </>
  );
}
